// Prepress home — the shared team board scoped per site (Exeter /
// Buckingham). docs/features/prepress-orchestration/17_safe_to_print_gate.md
// is the design doc; 18_safe_to_print_implementation_plan.md's "C — frontend:
// off mock data" is what shaped this file's current split between generated
// wire types (api-types.ts) and mock DATA (this file).
//
// The gate queue, blocked runs (with their ticket inlined), stray tickets and
// in-flight work are all backed for real by `GET /views/prepress/home` now —
// see `getPrepressHome` on the real adapter. This file supplies the mock
// adapter's data for that same shape, so Storybook, Playwright and mock
// previews keep working unchanged.
//
// This is a shared TEAM board, never personalised — "your tickets"/"assigned
// to you" framing is deliberately absent throughout (doc decision 6: "a good
// place to see those [tickets]", not a personal queue).

import type {
  PrepressBlockedRun,
  PrepressGateEvidenceItem,
  PrepressGateJob,
  PrepressHome,
  PrepressInFlightJob,
  PrepressSite,
  SafeToPrintChecklist,
  TicketViewItem,
} from "../data/api-types";

export const PREPRESS_SITE_LABELS: Record<PrepressSite, string> = {
  exeter: "Exeter",
  buckingham: "Buckingham",
};

// ── The confirm checklist ────────────────────────────────────────────────
//
// The six items and their `checklist_version` are business wording owned by
// the backend (`prepress_pipeline_module/models/safe_to_print.py`), served
// through `GET /prepress/runs/safe-to-print-checklist` — the frontend never
// hardcodes the label text (frontend-architecture.md "Business logic stays
// backend"). This mock mirrors that response for the mock adapter to serve;
// it is a copy of what the backend would say today, not a second source of
// truth the page reads directly.
export const mockSafeToPrintChecklist: SafeToPrintChecklist = {
  checklist_version: 1,
  items: [
    {
      id: "imposition_matches_finishing",
      label: "Imposition is right for the finishing (fold/trim/stitch dummy)",
    },
    {
      id: "separations_and_plates_match",
      label: "Separations and plate count match the job, spots included",
    },
    { id: "stock_matches_job_sheet", label: "Stock matches the job sheet" },
    {
      id: "special_processes_in_register",
      label: "Special processes present and in register (die, foil, spot UV)",
    },
    {
      id: "file_matches_customer_signoff",
      label: "The file going to plate is the one the customer signed off",
    },
    {
      id: "hand_work_included",
      label: "Any hand-work since the last automated step is in this file",
    },
  ],
};

// The checklist item the hand-imposed sub-checkbox nests under — a stable,
// well-known backend item id (see `SAFE_TO_PRINT_CHECKLIST` in
// `modules/prepress_pipeline_module/models/safe_to_print.py`), not business
// wording, so it is fine to reference directly for UI structure. The
// sub-checkbox itself has no item id: it is recorded as `hand_worked` on
// `RecordSafeToPrintRequest`, independently of this parent tick.
export const PREPRESS_GATE_HAND_WORK_ITEM_ID = "hand_work_included";

// ── Helpers ──────────────────────────────────────────────────────────────

// Relative to real wall-clock time (not mockToday) so "2h"-style ages read
// sensibly whenever the prototype is actually opened, rather than decaying
// against a fixed calendar day.
function hoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}

function evidence(
  id: string,
  label: string,
  detail: string,
  opts: {
    status?: PrepressGateEvidenceItem["status"];
    reference?: string;
  } = {},
): PrepressGateEvidenceItem {
  return {
    id,
    label,
    detail,
    status: opts.status ?? "ok",
    ...(opts.reference ? { reference: opts.reference } : {}),
  };
}

function ticket(params: {
  id: string;
  title: string;
  status: TicketViewItem["status"];
  priority?: TicketViewItem["priority"];
  created_at: string;
  assignee_user_name?: string;
  assigned_to_user_id?: string;
  owning_department_name: string;
  owning_department_site: PrepressSite;
  owning_department_slug: string;
  subject_label?: string;
}): TicketViewItem {
  return {
    _self: `/tickets/${params.id}`,
    id: params.id,
    ticket_id: params.id,
    title: params.title,
    // Prepress tickets are all raised by the pipeline on a stage failure,
    // never typed by a person.
    is_system_generated: true,
    status: params.status,
    priority: params.priority ?? "normal",
    created_at: params.created_at,
    created_by_user_id: "system",
    actioned: params.assigned_to_user_id != null,
    assignee_absent_today: false,
    assigned_to_user_id: params.assigned_to_user_id ?? null,
    assignee_user_name: params.assignee_user_name ?? null,
    owning_department_id: params.owning_department_slug,
    owning_department_name: params.owning_department_name,
    owning_department_site: params.owning_department_site,
    owning_department_slug: params.owning_department_slug,
    subject_label: params.subject_label ?? null,
    watcher_count: 0,
    type: "ticket",
  };
}

// ── Exeter — litho-heavy: KBA-105, agfa-avalon plate device, apogee rip ────

const exeterGateJobs: PrepressGateJob[] = [
  {
    type: "prepress_gate_job",
    run_id: "run-gate-100412",
    job_reference: "J-100412",
    customer_id: "cust_412",
    customer_name: "ACME Ltd",
    product_type_id: 4412,
    product_name: "8pp folded leaflet",
    site: "exeter",
    method: "litho",
    revision: 2,
    ready_at: hoursAgo(1.25),
    evidence: [
      evidence("preflight", "preflight", "0 blocking · 3 fixable, fixed"),
      evidence("proof", "proof", "approved by customer, rev 2, 14:22"),
      evidence("imposition", "imposition", "b1-8up → imposed.pdf"),
      evidence("rip", "rip", "apogee-q1 · dispatch #7741"),
      evidence("route", "route", "EXETER01 · KBA-105 · agfa-avalon"),
    ],
    _self: "/prepress/runs/run-gate-100412",
  },
  {
    type: "prepress_gate_job",
    run_id: "run-gate-100415",
    job_reference: "J-100415",
    customer_id: "cust_415",
    customer_name: "Trevithick Engineering",
    product_type_id: 4404,
    product_name: "4pp leaflet",
    site: "exeter",
    method: "litho",
    revision: 1,
    ready_at: hoursAgo(4.5),
    evidence: [
      evidence("preflight", "preflight", "0 blocking · 0 fixable"),
      evidence("proof", "proof", "approved by customer, rev 1, 09:40"),
      evidence("imposition", "imposition", "b2-4up → imposed.pdf"),
      evidence("rip", "rip", "apogee-q1 · dispatch #7738"),
      evidence("route", "route", "EXETER01 · KBA-105 · agfa-avalon"),
    ],
    _self: "/prepress/runs/run-gate-100415",
  },
  {
    type: "prepress_gate_job",
    run_id: "run-gate-100430",
    job_reference: "J-100430",
    customer_id: "cust_430",
    customer_name: "Meadowbrook Vets",
    product_type_id: 4801,
    product_name: "saddle-stitched booklet",
    site: "exeter",
    method: "litho",
    revision: 1,
    ready_at: hoursAgo(2.75),
    evidence: [
      evidence("preflight", "preflight", "0 blocking · 1 fixable, fixed"),
      evidence("proof", "proof", "approved by customer, rev 1, 11:05"),
      evidence("imposition", "imposition", "saddle-stitch-12pp → imposed.pdf"),
      evidence("rip", "rip", "apogee-q2 · dispatch #7739"),
      // The warning case: 28 of 37 presses currently plan UNROUTED (doc
      // §"A route showing UNROUTED must render as a warning, not a tick").
      evidence(
        "route",
        "route",
        "UNROUTED — no route configured for press 'Litho 5'",
        {
          status: "warning",
          reference: "press_routing.csv",
        },
      ),
    ],
    _self: "/prepress/runs/run-gate-100430",
  },
  {
    type: "prepress_gate_job",
    run_id: "run-gate-100441",
    job_reference: "J-100441",
    customer_id: "cust_441",
    customer_name: "Dunmore Presentation Co",
    product_type_id: 4290,
    product_name: "presentation folder",
    site: "exeter",
    method: "litho",
    revision: 1,
    ready_at: hoursAgo(0.6),
    evidence: [
      evidence("preflight", "preflight", "0 blocking · 2 fixable, fixed"),
      evidence("proof", "proof", "approved by customer, rev 1, 13:58"),
      evidence("imposition", "imposition", "b1-2up → imposed.pdf"),
      evidence("rip", "rip", "apogee-q1 · dispatch #7742"),
      evidence("route", "route", "EXETER01 · KBA-105 · agfa-avalon"),
    ],
    _self: "/prepress/runs/run-gate-100441",
  },
  // Held, not failed (docs/features/prepress-orchestration/
  // 17_safe_to_print_gate.md, "the decision, from prepress"): a hold keeps
  // the job parked at the gate rather than failing it. Was previously
  // modelled as a `failed` row in `exeterBlockedRuns` below — moved here
  // once the rejection-recovery decision landed, keeping the same operator
  // and reason text (a good realistic example) rather than inventing a new
  // one.
  {
    type: "prepress_gate_job",
    run_id: "run-gate-71795",
    job_reference: "J-71795",
    customer_id: "cust_073",
    customer_name: "Fenwick Bindery Supplies",
    product_type_id: 4801,
    product_name: "perfect-bound catalogue",
    site: "exeter",
    method: "litho",
    revision: 2,
    ready_at: "2026-09-08T15:05:00Z",
    evidence: [
      evidence("preflight", "preflight", "0 blocking · 1 fixable, fixed"),
      evidence("proof", "proof", "approved by customer, rev 2, 14:50"),
      evidence("imposition", "imposition", "perfect-bound-16pp → imposed.pdf"),
      evidence("rip", "rip", "apogee-q2 · dispatch #7701"),
      evidence("route", "route", "EXETER01 · KBA-105 · agfa-avalon"),
    ],
    hold: {
      operator: "j.harker",
      held_at: "2026-09-08T16:20:00Z",
      reason:
        "spine width doesn't match the bound sample, hold for re-estimate",
    },
    _self: "/prepress/runs/run-gate-71795",
  },
];

// ── Exeter — blocked (failed / non-routine awaiting_review), each carrying
// its open prepress ticket inline where one exists. Continuity note: these
// mirror four of the five scenarios the former standalone problem-queue page
// showed (job references, stages, messages) — the standalone
// `listProblemRuns` queue surface (and its mock fixtures) was removed once
// PrepressHomePage's Blocked section moved onto the composed
// `getPrepressHome`. This file's rows are independent copies the mock
// adapter serves through `getPrepressHome`'s composed `blocked` bucket,
// with a site and (where applicable) an inlined ticket added — fields the
// standalone queue's `ProblemRunResponse` never carried. The fifth
// scenario, J-71795, now lives in `exeterGateJobs` above as a *held* gate
// job: the gate has no rejection path any more, so what used to be a
// rejection is a hold that keeps the job at the gate — see the
// rejection-recovery decision note there.

const exeterBlockedRuns: PrepressBlockedRun[] = [
  {
    type: "prepress_blocked_run",
    run_id: "run-blocked-71822",
    job_reference: "J-71822",
    customer_id: "cust_012",
    site: "exeter",
    stage: "preflight",
    status: "failed",
    message:
      "callas pdfToolbox: server unreachable after 30s (PREPRESS_CALLAS_ENDPOINT timeout)",
    revision: 1,
    recorded_at: "2026-09-09T07:41:00Z",
    ticket: {
      id: "4501",
      title: "Preflight failing repeatedly on J-71822",
      status: "in_progress",
      priority: "high",
      assignee_user_name: "R. Hale",
      created_at: "2026-09-09T07:45:00Z",
      _self: "/tickets/4501",
    },
    _self: "/prepress/runs/run-blocked-71822",
  },
  {
    type: "prepress_blocked_run",
    run_id: "run-blocked-71838",
    job_reference: "J-71838",
    customer_id: "cust_022",
    site: "exeter",
    stage: "preflight",
    status: "awaiting_review",
    message:
      "Blocking preflight issue: image resolution 96dpi below the 250dpi minimum on 2 pages",
    revision: 1,
    recorded_at: "2026-09-09T08:05:00Z",
    ticket: {
      id: "4502",
      title: "Low-res images on J-71838 need customer confirmation",
      status: "open",
      priority: "normal",
      assignee_user_name: null,
      created_at: "2026-09-09T08:06:00Z",
      _self: "/tickets/4502",
    },
    _self: "/prepress/runs/run-blocked-71838",
  },
  {
    type: "prepress_blocked_run",
    run_id: "run-blocked-71844",
    job_reference: "J-71844",
    customer_id: "cust_048",
    site: "exeter",
    stage: "correction",
    status: "awaiting_review",
    message: "Operator-applied correction awaiting sign-off before re-rip",
    revision: 1,
    recorded_at: "2026-09-09T08:20:00Z",
    ticket: null,
    _self: "/prepress/runs/run-blocked-71844",
  },
  {
    type: "prepress_blocked_run",
    run_id: "run-blocked-71850",
    job_reference: "J-71850",
    customer_id: "cust_048",
    site: "exeter",
    stage: "imposition",
    status: "failed",
    message:
      "No layout template resolved for press 'Litho 5' — layout_spec_ref is UNLAID",
    revision: 1,
    recorded_at: "2026-09-09T06:52:00Z",
    ticket: null,
    _self: "/prepress/runs/run-blocked-71850",
  },
  // J-71795 (safe_to_print, held) moved to `exeterGateJobs` above — under
  // the rejection-recovery decision a hold parks the job at the gate rather
  // than failing it.
];

const exeterOpenTickets: TicketViewItem[] = [
  ticket({
    id: "4468",
    title: "Plate scratched on J-100390",
    status: "in_progress",
    priority: "high",
    created_at: hoursAgo(5),
    assignee_user_name: "R. Hale",
    assigned_to_user_id: "user-r-hale",
    owning_department_name: "Prepress – Exeter",
    owning_department_site: "exeter",
    owning_department_slug: "prepress-exeter",
  }),
];

const exeterInFlight: PrepressInFlightJob[] = [
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100422",
    job_reference: "J-100422",
    customer_id: "cust_422",
    customer_name: "Marsh & Co",
    site: "exeter",
    current_stage: "rip",
    stage_label: "rip running",
    _self: "/prepress/runs/run-flight-100422",
  },
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100427",
    job_reference: "J-100427",
    customer_id: "cust_427",
    customer_name: "Otterbridge Financial",
    site: "exeter",
    current_stage: "imposition",
    stage_label: "imposition queued",
    _self: "/prepress/runs/run-flight-100427",
  },
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100433",
    job_reference: "J-100433",
    customer_id: "cust_433",
    customer_name: "Kestrel Vineyards",
    site: "exeter",
    current_stage: "preflight",
    stage_label: "preflight running",
    _self: "/prepress/runs/run-flight-100433",
  },
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100436",
    job_reference: "J-100436",
    customer_id: "cust_436",
    customer_name: "Harbourside Cafe Group",
    site: "exeter",
    current_stage: "correction",
    stage_label: "correction applied",
    _self: "/prepress/runs/run-flight-100436",
  },
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100438",
    job_reference: "J-100438",
    customer_id: "cust_438",
    customer_name: "Bramwell & Sons",
    site: "exeter",
    current_stage: "proofing",
    stage_label: "proofing — awaiting customer",
    _self: "/prepress/runs/run-flight-100438",
  },
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100440",
    job_reference: "J-100440",
    customer_id: "cust_440",
    customer_name: "Pelham Estate Agents",
    site: "exeter",
    current_stage: "rip",
    stage_label: "rip queued",
    _self: "/prepress/runs/run-flight-100440",
  },
];

export const mockPrepressHomeExeter: PrepressHome = {
  site: "exeter",
  // The artwork family's lists are the mock adapter's (`mockPrepressHomeArtwork`).
  artwork: null,
  safe_to_print: exeterGateJobs,
  blocked: exeterBlockedRuns,
  open_tickets: exeterOpenTickets,
  in_flight: exeterInFlight,
};

// ── Buckingham — digital: Ricoh -> Fiery, HP Indigo 12000 -> DFE ───────────
// No plate engine, no rip engine of its own; the press RIPs itself.

const buckinghamGateJobs: PrepressGateJob[] = [
  {
    type: "prepress_gate_job",
    run_id: "run-gate-100460",
    job_reference: "J-100460",
    customer_id: "cust_460",
    customer_name: "Castlewood Media",
    product_type_id: 4110,
    product_name: "business cards",
    site: "buckingham",
    method: "digital",
    revision: 1,
    ready_at: hoursAgo(0.4),
    evidence: [
      evidence("preflight", "preflight", "0 blocking · 0 fixable"),
      evidence("proof", "proof", "approved by customer, rev 1, 14:10"),
      evidence("imposition", "imposition", "digital-2up → imposed.pdf"),
      // No `rip` row here — the HP Indigo RIPs itself via its own DFE, so
      // there is no separate rip-engine dispatch to report on.
      evidence("route", "route", "BUCKINGHAM01 · HP Indigo 12000 (DFE)"),
    ],
    _self: "/prepress/runs/run-gate-100460",
  },
];

const buckinghamInFlight: PrepressInFlightJob[] = [
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100463",
    job_reference: "J-100463",
    customer_id: "cust_463",
    customer_name: "Overton Cycle Works",
    site: "buckingham",
    current_stage: "proofing",
    stage_label: "proofing — awaiting customer",
    _self: "/prepress/runs/run-flight-100463",
  },
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100465",
    job_reference: "J-100465",
    customer_id: "cust_465",
    customer_name: "Lindfield Nursery School",
    site: "buckingham",
    current_stage: "preflight",
    stage_label: "preflight running",
    _self: "/prepress/runs/run-flight-100465",
  },
  {
    type: "prepress_in_flight_job",
    run_id: "run-flight-100467",
    job_reference: "J-100467",
    customer_id: "cust_467",
    customer_name: "Quennell & Barr Solicitors",
    site: "buckingham",
    current_stage: "imposition",
    stage_label: "imposition queued",
    _self: "/prepress/runs/run-flight-100467",
  },
];

export const mockPrepressHomeBuckingham: PrepressHome = {
  site: "buckingham",
  artwork: null,
  safe_to_print: buckinghamGateJobs,
  // No open runs blocked and no prepress-buckingham tickets right now — the
  // honest empty state.
  blocked: [],
  open_tickets: [],
  in_flight: buckinghamInFlight,
};

export const mockPrepressHomeBySite: Record<PrepressSite, PrepressHome> = {
  exeter: mockPrepressHomeExeter,
  buckingham: mockPrepressHomeBuckingham,
};
